// logger.js — thin console wrapper that also ships every call to PostHog:
// errors go to exception tracking (captureException), everything else goes
// as a `frontend_log` event with a `level` property. This is on top of
// console.error/warn already being captured into session replays
// (posthog.js `enable_recording_console_log`) — so logs are queryable and
// alertable in PostHog directly, not just visible inside a replay.
//
// Cost note: unlike backend logs (dedicated OTLP Logs product, separate
// quota), these ship as regular PostHog events and count against the
// Product Analytics event quota shared with real business events
// (add_to_cart, checkout_started, ...). Keep debug/info out of hot paths
// (render loops, socket message handlers) to avoid burning through it.
import posthog from '../posthog';

const safeArg = (a) => {
  if (a instanceof Error) return { message: a.message, stack: a.stack };
  try {
    JSON.stringify(a);
    return a;
  } catch {
    return String(a);
  }
};

const send = (level, args) => {
  const err = args.find((a) => a instanceof Error);
  if (err) {
    posthog.captureException(err, {
      level,
      context: args.filter((a) => a !== err).map(safeArg),
    });
  } else {
    posthog.capture('frontend_log', { level, args: args.map(safeArg) });
  }
};

const logger = {
  debug: (...args) => {
    console.debug(...args);
    send('debug', args);
  },
  info: (...args) => {
    console.info(...args);
    send('info', args);
  },
  warn: (...args) => {
    console.warn(...args);
    send('warn', args);
  },
  error: (...args) => {
    console.error(...args);
    send('error', args);
  },
};

export default logger;
